import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  orderBy, 
  // where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  // DocumentData,
  // QuerySnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';

// 기존 React Native 앱과 동일한 데이터 구조
export interface CardData {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  website?: string;
  address?: string;
  bio?: string;
  profile?: {
    name: string;
    email?: string;
    phone?: string;
    company?: string;
    position?: string;
    website?: string;
    address?: string;
  };
  customUrl?: string;
  customPath?: string;
  customDomain?: string;
  shareUrl?: string;
  socialLinks?: {
    linkedin?: string;
    twitter?: string;
    instagram?: string;
  };
  // 앱 버전과 동일한 추가 필드들
  instagramId?: string;
  threadsId?: string;
  youtubeId?: string;
  linkedinId?: string;
  facebookId?: string;
  tiktokId?: string;
  kakaoChannelUrl?: string;
  companyAddress?: string;
  memo?: string;
  introText?: string;
  servicesIntro?: string;
  calendlyLink?: string;
  feedbackFormLink?: string;
  // 프로필 사진 필드
  photoUrl?: string;
  // 포트폴리오 갤러리 필드
  portfolioImages?: string[];
  // 색상 관련 필드들
  cardButtonColor?: string;
  cardBackgroundColor?: string;
  ownerUid?: string;
  createdAt?: any;
  updatedAt?: any;
  tags?: string[];
  isPublic?: boolean;
}

// URL을 정규화하는 함수 (http:// 또는 https:// 없으면 https:// 추가)
function normalizeUrl(url: string | undefined): string | undefined {
  if (!url || typeof url !== 'string') return url;
  
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return url;
  
  // 이미 http:// 또는 https://로 시작하면 그대로 반환
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  
  // 도메인이 포함되어 있으면 https:// 추가
  if (trimmedUrl.includes('.') && !trimmedUrl.includes(' ')) {
    return `https://${trimmedUrl}`;
  }
  
  return url;
}

// undefined 값을 Firestore에 저장하지 않도록 제거하는 유틸리티
function removeUndefinedDeep<T>(input: T): T {
  if (Array.isArray(input)) {
    // 배열 내부 항목을 재귀적으로 정리
    const cleanedArray = (input as unknown[])
      .map((item) => removeUndefinedDeep(item))
      .filter((item) => item !== undefined);
    return cleanedArray as unknown as T;
  }
  if (input !== null && typeof input === 'object') {
    // Firestore FieldValue 같은 특수 객체는 그대로 반환
    const proto = Object.getPrototypeOf(input);
    if (proto !== Object.prototype) {
      return input;
    }
    const result: Record<string, unknown> = {};
    Object.entries(input as Record<string, unknown>).forEach(([key, value]) => {
      if (value === undefined) return;
      const cleaned = removeUndefinedDeep(value as never);
      if (cleaned !== undefined) {
        result[key] = cleaned;
      }
    });
    return result as unknown as T;
  }
  // 기본 타입은 그대로 반환 (undefined는 상위에서 걸러짐)
  return input;
}

// 카드 서비스 클래스
export class CardService {
  // 사용자의 카드 목록 가져오기 (기존 앱과 동일한 구조)
  static async getUserCards(userId: string): Promise<CardData[]> {
    try {
      console.log('🔍 [CardService] 사용자 카드 가져오기:', userId);
      
      const cardsRef = collection(db, 'users', userId, 'cards');
      const q = query(cardsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const cards: CardData[] = [];
      snapshot.forEach((doc) => {
        cards.push({ id: doc.id, ...doc.data() } as CardData);
      });
      
      console.log('✅ [CardService] 카드 가져오기 성공:', cards.length);
      return cards;
    } catch (error) {
      console.error('❌ [CardService] 카드 가져오기 실패:', error);
      throw error;
    }
  }

  // 실시간 카드 목록 구독 (기존 앱과 동일한 구조)
  static subscribeToUserCards(userId: string, callback: (cards: CardData[]) => void) {
    try {
      console.log('👂 [CardService] 실시간 카드 구독 시작:', userId);
      
      const cardsRef = collection(db, 'users', userId, 'cards');
      const q = query(cardsRef, orderBy('createdAt', 'desc'));
      
      return onSnapshot(q, (snapshot) => {
        const cards: CardData[] = [];
        snapshot.forEach((doc) => {
          cards.push({ id: doc.id, ...doc.data() } as CardData);
        });
        
        console.log('📡 [CardService] 실시간 카드 업데이트:', cards.length);
        callback(cards);
      }, (error) => {
        console.error('❌ [CardService] 실시간 구독 오류:', error);
        // createdAt 누락 등으로 orderBy 쿼리 실패 시 fallback
        if (error.code === 'failed-precondition' || error.message?.includes('no index')) {
          console.log('🔄 [CardService] Fallback 쿼리 실행');
          getDocs(cardsRef).then(snap => {
            const fallback: CardData[] = [];
            snap.forEach((doc) => {
              fallback.push({ id: doc.id, ...doc.data() } as CardData);
            });
            callback(fallback);
          });
        }
      });
    } catch (error) {
      console.error('❌ [CardService] 구독 설정 실패:', error);
      throw error;
    }
  }

  // 특정 카드 가져오기
  static async getCard(userId: string, cardId: string): Promise<CardData | null> {
    try {
      console.log('🔍 [CardService] 카드 상세 가져오기:', { userId, cardId });
      
      const cardRef = doc(db, 'users', userId, 'cards', cardId);
      const cardDoc = await getDoc(cardRef);
      
      if (cardDoc.exists()) {
        const cardData = { id: cardDoc.id, ...cardDoc.data() } as CardData;
        console.log('✅ [CardService] 카드 상세 가져오기 성공');
        return cardData;
      } else {
        console.log('⚠️ [CardService] 카드를 찾을 수 없음');
        return null;
      }
    } catch (error) {
      console.error('❌ [CardService] 카드 상세 가져오기 실패:', error);
      throw error;
    }
  }

  // 공개 카드 가져오기 (customUrl로)
  static async getPublicCard(customUrl: string): Promise<CardData | null> {
    try {
      console.log('🔍 [CardService] 공개 카드 가져오기:', customUrl);
      
      // customLinks 컬렉션에서 매핑 찾기
      const linkRef = doc(db, 'customLinks', customUrl);
      const linkDoc = await getDoc(linkRef);
      
      if (!linkDoc.exists()) {
        console.log('⚠️ [CardService] 커스텀 링크를 찾을 수 없음');
        return null;
      }
      
      const linkData = linkDoc.data();
      const cardId = linkData.cardId;
      const userId = linkData.userId;
      
      if (!cardId || !userId) {
        console.log('⚠️ [CardService] 링크 데이터가 불완전함');
        return null;
      }
      
      // 실제 카드 데이터 가져오기
      return await this.getCard(userId, cardId);
    } catch (error) {
      console.error('❌ [CardService] 공개 카드 가져오기 실패:', error);
      throw error;
    }
  }

  // customUrl 중복 체크
  static async checkCustomUrlAvailability(customUrl: string): Promise<boolean> {
    try {
      console.log('🔍 [CardService] customUrl 중복 체크:', customUrl);
      
      const linkRef = doc(db, 'customLinks', customUrl);
      const linkDoc = await getDoc(linkRef);
      
      const isAvailable = !linkDoc.exists();
      console.log('✅ [CardService] customUrl 체크 완료:', { customUrl, isAvailable });
      
      return isAvailable;
    } catch (error) {
      console.error('❌ [CardService] customUrl 중복 체크 실패:', error);
      throw error;
    }
  }

  // 새 카드 생성 (기존 앱과 동일한 구조)
  static async createCard(userId: string, cardData: Omit<CardData, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      console.log('➕ [CardService] 새 카드 생성:', { userId, cardData });
      
      // customUrl이 있으면 중복 체크
      if (cardData.customUrl) {
        const isAvailable = await this.checkCustomUrlAvailability(cardData.customUrl);
        if (!isAvailable) {
          throw new Error('CUSTOM_URL_ALREADY_EXISTS');
        }
      }
      
      // URL 필드들을 정규화
      const normalizedCardData = {
        ...cardData,
        website: normalizeUrl(cardData.website),
        calendlyLink: normalizeUrl(cardData.calendlyLink),
        feedbackFormLink: normalizeUrl(cardData.feedbackFormLink),
        kakaoChannelUrl: normalizeUrl(cardData.kakaoChannelUrl),
      };
      
      const finalCardData = {
        ...normalizedCardData,
        ownerUid: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      // Firestore에 저장 불가한 undefined 필드를 제거
      const dataToSave = removeUndefinedDeep(finalCardData);
      
      const cardsRef = collection(db, 'users', userId, 'cards');
      const newCardRef = await addDoc(cardsRef, dataToSave);
      
      console.log('✅ [CardService] 카드 생성 성공:', newCardRef.id);
      
      // customPath가 있으면 customLinks 컬렉션에 매핑 생성 (앱과 동일)
      if (cardData.customPath) {
        try {
          const linkRef = doc(db, 'customLinks', cardData.customPath);
          await setDoc(linkRef, {
            userId: userId,
            cardId: newCardRef.id,
            status: 'active',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }, { merge: true });
          
          console.log('✅ [CardService] customLinks 매핑 생성 성공:', cardData.customPath);
        } catch (linkError) {
          console.error('⚠️ [CardService] customLinks 매핑 생성 실패:', linkError);
          // 매핑 생성 실패해도 카드 생성은 성공으로 처리
        }
      }
      
      return newCardRef.id;
    } catch (error) {
      console.error('❌ [CardService] 카드 생성 실패:', error);
      throw error;
    }
  }

  // 카드 업데이트
  static async updateCard(userId: string, cardId: string, updates: Partial<CardData>): Promise<void> {
    try {
      console.log('✏️ [CardService] 카드 업데이트:', { userId, cardId, updates });
      
      // URL 필드들을 정규화
      const normalizedUpdates = {
        ...updates,
        website: normalizeUrl(updates.website),
        calendlyLink: normalizeUrl(updates.calendlyLink),
        feedbackFormLink: normalizeUrl(updates.feedbackFormLink),
        kakaoChannelUrl: normalizeUrl(updates.kakaoChannelUrl),
      };
      
      // undefined 값 제거
      const cleanUpdates = removeUndefinedDeep(normalizedUpdates);
      
      const cardRef = doc(db, 'users', userId, 'cards', cardId);
      await updateDoc(cardRef, {
        ...cleanUpdates,
        updatedAt: serverTimestamp(),
      });
      
             // customPath가 변경되었으면 customLinks 컬렉션도 업데이트 (앱과 동일)
       if (updates.customPath) {
         try {
           const linkRef = doc(db, 'customLinks', updates.customPath);
           await setDoc(linkRef, {
             userId: userId,
             cardId: cardId,
             status: 'active',
             createdAt: serverTimestamp(),
             updatedAt: serverTimestamp(),
           }, { merge: true });
           
           console.log('✅ [CardService] customLinks 매핑 업데이트 성공:', updates.customPath);
         } catch (linkError) {
           console.error('⚠️ [CardService] customLinks 매핑 업데이트 실패:', linkError);
           // 매핑 업데이트 실패해도 카드 업데이트는 성공으로 처리
         }
       }
      
      console.log('✅ [CardService] 카드 업데이트 성공');
    } catch (error) {
      console.error('❌ [CardService] 카드 업데이트 실패:', error);
      throw error;
    }
  }

  // 카드 삭제 (앱과 동일한 완전 삭제 로직)
  static async deleteCard(userId: string, cardId: string): Promise<void> {
    try {
      console.log('🗑️ [CardService] 카드 완전 삭제 시작:', { userId, cardId });
      
      // 1. 인보이스 하위 컬렉션 삭제
      console.log('📄 [CardService] 인보이스 삭제 중...');
      try {
        const invoicesRef = collection(db, 'users', userId, 'cards', cardId, 'invoices');
        const invoicesSnapshot = await getDocs(invoicesRef);
        
        if (!invoicesSnapshot.empty) {
          console.log(`📄 [CardService] ${invoicesSnapshot.size}개 인보이스 발견`);
          
          // Firebase Storage에서 PDF 파일들 삭제
          const { getStorage, ref: storageRef, deleteObject } = await import('firebase/storage');
          const storage = getStorage();
          
          for (const invoiceDoc of invoicesSnapshot.docs) {
            try {
              // Storage에서 PDF 파일 삭제
              const pdfStoragePath = `users/${userId}/cards/${cardId}/invoices/${invoiceDoc.id}.pdf`;
              const pdfRef = storageRef(storage, pdfStoragePath);
              try {
                await deleteObject(pdfRef);
                console.log(`✅ [CardService] PDF 삭제됨: ${pdfStoragePath}`);
              } catch (pdfError: any) {
                // 404 에러(파일이 이미 존재하지 않음)는 정상적인 상황이므로 무시
                if (pdfError.code === 'storage/object-not-found') {
                  console.log(`ℹ️ [CardService] PDF 파일이 이미 존재하지 않음: ${pdfStoragePath}`);
                } else {
                  console.warn(`⚠️ [CardService] PDF 삭제 실패 (${pdfStoragePath}):`, pdfError);
                }
              }
              
              // Firestore 인보이스 문서 삭제
              await deleteDoc(invoiceDoc.ref);
              console.log(`✅ [CardService] 인보이스 문서 삭제됨: ${invoiceDoc.id}`);
            } catch (docError) {
              console.warn(`⚠️ [CardService] 인보이스 삭제 실패 (${invoiceDoc.id}):`, docError);
            }
          }
        } else {
          console.log('✅ [CardService] 인보이스: 삭제할 데이터 없음');
        }
      } catch (invoicesError) {
        console.warn('⚠️ [CardService] 인보이스 삭제 중 오류:', invoicesError);
      }

      // 2. 비즈니스 프로필 삭제 (카드별 + 일반)
      console.log('🏢 [CardService] 비즈니스 프로필 삭제 중...');
      try {
        // 카드별 비즈니스 프로필 삭제
        const cardBusinessProfileRef = doc(db, 'users', userId, 'cards', cardId, 'businessProfile', 'info');
        await deleteDoc(cardBusinessProfileRef);
        console.log('✅ [CardService] 카드별 비즈니스 프로필 삭제됨');
        
        // 일반 비즈니스 프로필도 삭제 (명함 삭제 시 완전 초기화)
        const generalBusinessProfileRef = doc(db, 'users', userId, 'businessProfiles', 'current');
        await deleteDoc(generalBusinessProfileRef);
        console.log('✅ [CardService] 일반 비즈니스 프로필 삭제됨');
      } catch (profileError) {
        console.warn('⚠️ [CardService] 비즈니스 프로필 삭제 실패:', profileError);
      }

      // 3. publicCards 매핑 삭제
      console.log('🌐 [CardService] publicCards 매핑 삭제 중...');
      try {
        const publicCardRef = doc(db, 'publicCards', cardId);
        await deleteDoc(publicCardRef);
        console.log('✅ [CardService] publicCards 매핑 삭제됨');
      } catch (publicCardError) {
        console.warn('⚠️ [CardService] publicCards 매핑 삭제 실패:', publicCardError);
      }

      // 4. customLinks 매핑 삭제 (커스텀 URL 삭제)
      console.log('🔗 [CardService] customLinks 매핑 삭제 중...');
      try {
        // 먼저 카드 데이터를 읽어서 customPath 확인
        const cardRef = doc(db, 'users', userId, 'cards', cardId);
        const cardSnapshot = await getDoc(cardRef);
        
        if (cardSnapshot.exists()) {
          const cardData = cardSnapshot.data();
          if (cardData.customPath) {
            const customLinkRef = doc(db, 'customLinks', cardData.customPath);
            await deleteDoc(customLinkRef);
            console.log(`✅ [CardService] customLinks 매핑 삭제됨: ${cardData.customPath}`);
          }
        }
      } catch (customLinkError) {
        console.warn('⚠️ [CardService] customLinks 매핑 삭제 실패:', customLinkError);
      }

      // 5. 메인 카드 문서 삭제
      console.log('📄 [CardService] 메인 카드 문서 삭제 중...');
      const cardRef = doc(db, 'users', userId, 'cards', cardId);
      await deleteDoc(cardRef);
      console.log('✅ [CardService] 메인 카드 문서 삭제됨');

      console.log('🎉 [CardService] 카드 완전 삭제 완료!');
    } catch (error) {
      console.error('❌ [CardService] 카드 삭제 실패:', error);
      throw error;
    }
  }
} 